import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { FirstPackageClientEntry, PeriodKey, StaffPeriods } from '../types';
import { ConversionSegment, MAY_STILL_CONVERT_DAYS, SEGMENT_COLORS, SEGMENT_ORDER, countSegments, segmentOf } from '../utils/conversionSegments';
import { periodLabel } from '../utils/metrics';
import { shortPricingOption } from './ClientMovementCard';
import { OutcomeCell, SessionsCell, TABLE_LABELS } from './ConversionClientCells';
import { CONTROL_SX, FilterChip, SELECTED_SX } from './ConversionFilterControls';

export interface ConversionClientsTableProps {
  clients: FirstPackageClientEntry[];
  periods: StaffPeriods;
  sourceCreatedDate: string;
}

type SegmentFilter = 'all' | ConversionSegment;

/** Rows shown until "Show all"; a new period or segment starts again from here. */
export const ROW_PREVIEW = 10;

/** Fixed shares of the card's width, so the table fits it without scrolling sideways; the outcome caption gets the most. */
const COLUMNS = [
  { label: 'Client', width: '20%' },
  { label: 'First package', width: '19%' },
  { label: 'Sessions', width: '10%' },
  { label: 'Expired', width: '12%' },
  { label: 'Last visit', width: '12%' },
  { label: 'Outcome', width: '27%' },
];

const clientPath = (clientId: string) => `/clients/${clientId}`;

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = parseISO(value);
  return isValid(date) ? format(date, 'd MMM yyyy') : '—';
};

/** The row navigates on click too; stopping the link's click there keeps one history entry per click. */
const stopRowClick = (event: React.MouseEvent) => event.stopPropagation();

/** Only a plain left click opens the row's client; a modified click, or one that ends a text selection, does nothing. */
const isPlainRowClick = (event: React.MouseEvent): boolean =>
  !event.defaultPrevented &&
  event.button === 0 &&
  !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) &&
  !window.getSelection()?.toString();

/** Every client whose first package expired in one six-month period, filterable by how the package ended. */
export const ConversionClientsTable: React.FC<ConversionClientsTableProps> = ({ clients, periods, sourceCreatedDate }) => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<PeriodKey>('latest_6m');
  const [segment, setSegment] = useState<SegmentFilter>('all');
  const [showAll, setShowAll] = useState(false);

  const counts = countSegments(clients, period);
  const rows = clients.filter((entry) => entry.period === period && (segment === 'all' || segmentOf(entry) === segment));
  const visibleRows = showAll ? rows : rows.slice(0, ROW_PREVIEW);
  const periodWord = period === 'latest_6m' ? 'latest' : 'prior';
  const openClient = (event: React.MouseEvent, clientId: string) => {
    if (isPlainRowClick(event)) navigate(clientPath(clientId));
  };
  /** A different period or segment starts again from the preview; re-picking the current one leaves the list as it is. */
  const choosePeriod = (next: PeriodKey) => {
    if (next === period) return;
    setPeriod(next);
    setShowAll(false);
  };
  const chooseSegment = (next: SegmentFilter) => {
    if (next === segment) return;
    setSegment(next);
    setShowAll(false);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', columnGap: 1, rowGap: 1 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          aria-label="Period"
          value={period}
          onChange={(_event, value: PeriodKey | null) => {
            if (value) choosePeriod(value);
          }}
          sx={{
            mr: 1,
            // A segmented control: the shared shape, square inner corners, and a selected segment whose blue edge wins
            // on both sides over its neighbour's grey one.
            '& .MuiToggleButton-root': CONTROL_SX,
            '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': { ml: '-1px', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
            '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
            '& .MuiToggleButtonGroup-grouped.Mui-selected': { ...SELECTED_SX, zIndex: 1, borderLeft: '1px solid', borderLeftColor: 'primary.main' },
          }}
        >
          <ToggleButton value="latest_6m" title={periodLabel(periods.latest)}>
            Latest 6 months
          </ToggleButton>
          <ToggleButton value="prior_6m" title={periodLabel(periods.prior)}>
            Prior 6 months
          </ToggleButton>
        </ToggleButtonGroup>
        <FilterChip label="All" count={counts.expired} active={segment === 'all'} onClick={() => chooseSegment('all')} />
        {SEGMENT_ORDER.map((key) => (
          <FilterChip
            key={key}
            label={TABLE_LABELS[key]}
            count={counts[key]}
            color={SEGMENT_COLORS[key]}
            active={segment === key}
            onClick={() => chooseSegment(key)}
          />
        ))}
      </Stack>
      <TableContainer>
        <Table
          size="small"
          aria-label="First package clients"
          sx={{
            tableLayout: 'fixed',
            '& .MuiTableCell-root': { px: 1.25, py: 1, verticalAlign: 'top' },
          }}
        >
          <colgroup>
            {COLUMNS.map((column) => (
              <col key={column.label} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column.label}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((entry) => (
              <TableRow key={entry.clientId} hover sx={{ cursor: 'pointer' }} onClick={(event) => openClient(event, entry.clientId)}>
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={clientPath(entry.clientId)}
                    title={`Client ${entry.clientId}`}
                    underline="hover"
                    variant="body2"
                    sx={{ fontWeight: 600 }}
                    onClick={stopRowClick}
                  >
                    {entry.clientName ?? 'Unknown client'}
                  </Link>
                </TableCell>
                <TableCell>{shortPricingOption(entry.firstPricingOption)}</TableCell>
                <TableCell>
                  <SessionsCell entry={entry} />
                </TableCell>
                <TableCell>{formatDate(entry.firstExpiryDate)}</TableCell>
                <TableCell>{formatDate(entry.lastVisitDate)}</TableCell>
                <TableCell>
                  <OutcomeCell entry={entry} sourceCreatedDate={sourceCreatedDate} />
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length}>
                  <Typography variant="body2" color="text.secondary">
                    {segment === 'all'
                      ? `No first packages expired in the ${periodWord} 6 months`
                      : `No clients in this segment for the ${periodWord} 6 months`}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > ROW_PREVIEW ? (
        <Button size="small" variant="outlined" sx={{ mt: 1.5 }} onClick={() => setShowAll((current) => !current)}>
          {showAll ? 'Show fewer' : `Show all ${rows.length}`}
        </Button>
      ) : null}
      <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>
        {`Packages that expired in the last ${MAY_STILL_CONVERT_DAYS} days are marked “may still convert”.`}
      </Typography>
    </Box>
  );
};
